import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import {
  LoadFailed,
  NoMatches,
  NothingYet,
} from "@/components/shell/EmptyState";
import { Fab } from "@/components/shell/Fab";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Button } from "@/components/ui/button";
import { projectCreditNote } from "@/modules/gst/projections";
import { CreateCreditNote } from "@/modules/gst/screens/CreateCreditNote";
import { CreditNoteDetail } from "@/modules/gst/screens/CreditNoteDetail";
import type { SectionScreenProps } from "@/modules/types";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { useOpenRecord } from "@/routes/useOpenRecord";

/**
 * The Credit Notes list, the "New credit note" flow, and — in place of
 * either once a row is clicked or a note is created — the credit note
 * detail (all three, ticket #91).
 *
 * The create flow and the detail are local views swapped in here rather
 * than sections of their own, for the same reason `InvoiceList` swaps in
 * `InvoiceDetail` locally: `config/modules.ts` names only `credit-notes`,
 * so there is no route for the router to match a create form or a `:id`
 * against.
 */
export function CreditNotes({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const { recordId, open } = useOpenRecord();
  const [creating, setCreating] = useState(false);

  /*
    Fetched by id rather than found among the rows on screen. A selection in
    component state could only ever name a record the current page held; one
    in the URL is followed cold, from a page nobody has loaded (M2-13).
  */
  const noteQuery = useQuery({
    queryKey: ["gst", "credit-note", recordId],
    queryFn: () =>
      recordId === null ? null : data.invoicing.getCreditNote(recordId),
    enabled: recordId !== null,
  });

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];

  const query = useQuery({
    queryKey: ["gst", "credit-notes", filter, sort, limit, cursor],
    queryFn: () =>
      data.invoicing.listCreditNotes({ filter, sort, limit, cursor }),
  });

  if (creating) {
    return (
      <CreateCreditNote
        onCreated={(note) => {
          setCreating(false);
          open(note.id);
        }}
        onCancel={() => {
          setCreating(false);
        }}
      />
    );
  }

  if (query.isPending) return <TableSkeleton />;

  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {recordId !== null ? (
        <RecordDrawer title={noteQuery.data?.creditNoteNumber ?? "Credit note"}>
          {noteQuery.data ? (
            <CreditNoteDetail creditNote={noteQuery.data} />
          ) : (
            <TableSkeleton rows={3} />
          )}
        </RecordDrawer>
      ) : null}
      <div className="max-md:hidden flex shrink-0 justify-end">
        <Button
          size="sm"
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus />
          New credit note
        </Button>
      </div>
      <Fab
        label="New credit note"
        icon={<Plus aria-hidden />}
        onClick={() => {
          setCreating(true);
        }}
      />

      <DataTable
        fields={fields}
        rows={query.data.items}
        getRowId={(note) => note.id}
        project={projectCreditNote}
        sort={sort}
        onToggleSort={onToggleSort}
        onRowClick={(note) => {
          open(note.id);
        }}
        emptyState={
          filter.children.length > 0 ? (
            <NoMatches onClear={onClearFilter} />
          ) : (
            <NothingYet
              title="No credit notes yet"
              description="A credit note corrects an issued invoice. Nothing has been corrected yet."
              action={
                <Button
                  size="sm"
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  <Plus />
                  New credit note
                </Button>
              }
            />
          )
        }
        footer={
          <Pagination
            page={query.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
